import type { RouterHistory } from './router-history.ts';
import type { RouterFacade } from './router.facade';

export class RouterService implements RouterFacade {
  constructor(private history: RouterHistory) {}

  /** @param fallback - function that will be executed if there is no route to go back to, should navigate
   * @example
   * ```
   * // tries go go back, returns to the library if going back is not possible
   * router.back(() => router.library())
   * ```
   *
   * */
  back(fallback?: () => void) {
    this.history.back(fallback);
  }

  timetable() {
    this.history.navigate({ path: '/timetable' });
  }
  library() {
    this.history.navigate({ path: '/library' });
  }
  unsortedTopics() {
    this.history.navigate({ path: '/library/unsorted' });
  }
  folder(id: string) {
    this.history.navigate({ path: `/library/folder/${id}` });
  }
  topic(args: { id: string; courseId?: string }) {
    const { id, courseId } = args;
    this.history.navigate({
      path: `/library/topic/${id}`,
      query: courseId ? { courseId } : undefined,
    });
  }
  item(id: string) {
    this.history.navigate({ path: `/library/material/${id}` });
  }
  librarySearch() {
    this.history.navigate({ path: `/library/search` });
  }
  courses() {
    this.history.navigate({ path: `/course` });
  }
  course(id: string) {
    this.history.navigate({ path: `/course/${id}/content` });
  }
  presentMaterial(materialId: string) {
    this.history.navigate({ path: `/presentation/material/${materialId}` });
  }
  material(args: {
    id: string;
    view?: 'edit' | 'share' | 'evaluate' | 'preview';
    courseId?: string;
    ipi?: boolean;
    forceEditorTo?: 'new' | 'legacy';
  }) {
    const { view, id, courseId, ipi, forceEditorTo } = args;
    const currentRoute = this.history.currentRoute();

    type MaterialQueryParams = {
      view?: 'edit' | 'share' | 'evaluate' | 'preview';
      ipi?: 'true';
      courseId?: string;
      forceEditorTo?: 'new' | 'legacy';
    };

    const query: MaterialQueryParams = {
      view: view ?? 'edit',
    };
    if (ipi || currentRoute.query?.ipi === 'true') {
      query.ipi = 'true';
    }

    const routeForceEditorTo = currentRoute.query?.forceEditorTo;

    if (forceEditorTo) {
      query.forceEditorTo = forceEditorTo;
    } else if (
      routeForceEditorTo === 'new' ||
      routeForceEditorTo === 'legacy'
    ) {
      query.forceEditorTo = routeForceEditorTo;
    }
    if (typeof currentRoute.query?.courseId === 'string') {
      query.courseId = currentRoute.query.courseId;
    }
    if (courseId) {
      query.courseId = courseId;
    }

    this.history.navigate({
      path: `/library/material/${id}`,
      query,
    });
  }
  onboardingMaterial(materialId: string) {
    this.history.navigate({ path: `/library/material/${materialId}` });
  }

  studentAssignments(assignmentId?: string) {
    if (assignmentId) {
      this.history.navigate({
        path: '/student-ai',
        query: { newAssignment: assignmentId },
      });
    } else {
      this.history.navigate({ path: '/student-ai' });
    }
  }

  templatePreview(key: string) {
    this.history.navigate({ path: `/student-ai/templates/preview/${key}` });
  }

  assignmentPreview(assignmentId: string) {
    this.history.navigate({
      path: `/student-ai/assignments/preview/${assignmentId}`,
    });
  }

  thena(taskId?: string) {
    if (taskId) {
      this.history.navigate({ path: `/ai/chat/${taskId}` });
    } else {
      this.history.navigate({ path: '/ai' });
    }
  }

  upgrade() {
    this.history.navigate({ path: '/account/upgrade' });
  }

  ipi(topicId?: string) {
    this.history.navigate({
      path: '/ipi',
      query: topicId ? { topicId } : undefined,
    });
  }

  help(page: string) {
    this.history.navigate({ path: `/help/${page}` });
  }
}
